import { alertController } from "@ionic/vue";
import { acceptHMRUpdate, defineStore } from "pinia";
import { reactive, toRef, toRefs } from "vue";

import { usePlayer } from "@/composables/use-player.composable";
import { useToasts } from "@/composables/use-toasts.composable";
import { useSessionStore } from "@/stores/session.store";
import { usePlayerResume } from "@/stores/use-player-resume.composable";
import type { Product } from "@/types";

type State = {
  product: Product | undefined;
  isOpen: boolean;
};

export const usePlayerStore = defineStore(
  "player",
  () => {
    const state = reactive<State>({
      product: undefined,
      isOpen: false,
    });
    const toasts = useToasts();
    const sessionStore = useSessionStore();

    const player = usePlayer({
      transformSourceUrl(sourceUrl) {
        const { authToken } = sessionStore;

        if (!authToken) {
          throw new Error("Authentication is required");
        }

        const audioUrl = new URL(sourceUrl);

        audioUrl.searchParams.set("_token", authToken);

        return audioUrl.toString();
      },
      onError(error) {
        toasts.showError(error.message);
      },
    });
    const playerResume = usePlayerResume(player, toRef(state, "product"));

    function initialize(product: Product) {
      if (!product.tracks.length) {
        throw new Error("Product has no supported tracks");
      }

      if (!sessionStore.authToken) {
        throw new Error("Authentication is required");
      }

      if (player.isPlaybackRequested.value) {
        // Flush resume state for the old product before replacing it.
        player.pause();
      }

      state.product = product;
      state.isOpen = true;

      player.setTracklist(product.tracks, { autoplay: true });
    }

    async function openInPlayer(product: Product) {
      if (!product.canPlay) {
        const dialog = await alertController.create({
          header: "Unsupported audiobook",
          message: "This audiobook is not supported in the app yet.",
          buttons: ["OK"],
        });

        await dialog.present();
        return;
      }

      if (state.product?.id === product.id) {
        state.isOpen = true;

        if (player.ended.value) {
          await player.play();
        }

        return;
      }

      if (state.product && player.isPlaybackRequested.value) {
        const dialog = await alertController.create({
          header: "Switch audiobook",
          message:
            "Playback of the current audiobook will stop. Do you want to continue?",
          buttons: [
            { text: "Cancel", role: "cancel" },
            {
              text: "Continue",
              handler: () => initialize(product),
            },
          ],
        });

        await dialog.present();
        return;
      }

      initialize(product);
    }

    function reset() {
      playerResume.clear();
      state.product = undefined;
      state.isOpen = false;
      player.clearTracklist();
    }

    return {
      ...toRefs(state),
      tracks: player.tracks,
      options: player.options,
      status: player.status,
      timeElapsed: player.timeElapsed,
      duration: player.duration,
      buffered: player.buffered,
      position: player.position,
      currentTrack: player.currentTrack,
      nextTrack: player.nextTrack,
      prevTrack: player.prevTrack,
      cycleRepeatMode: player.cycleRepeatMode,
      play: player.play,
      pause: player.pause,
      toggle: player.toggle,
      seekTo: player.seekTo,
      seekForward: player.seekForward,
      seekBackward: player.seekBackward,
      skipTo: player.skipTo,
      skipForward: player.skipForward,
      skipBackward: player.skipBackward,
      openInPlayer,
      restore: playerResume.restore,
      reset,
    };
  },
  {
    persist: {
      pick: ["options"],
    },
  },
);

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(usePlayerStore, import.meta.hot));
}
